import * as pc from 'playcanvas';
import { gameState, resetGameState, setPlayerCount, setDifficulty } from './state.js';
import { LEVEL_CONFIG, MAX_LEVELS } from '../config/levels.js';
import { DIFFICULTY_CONFIG } from '../config/difficulty.js';

// Entities
import { createPlayer, resetPlayer, areAllPlayersDefeated } from '../entities/player.js';
import { createBoss, resetBoss } from '../entities/boss.js';
import { createMinion, updateMinion, canMinionAttack, minionAttack, clearMinions, getMaxMinions, getSpawnInterval } from '../entities/minion.js';
import { createArena, getCollisionObjects, clearArena, getArenaBounds } from '../entities/arena.js';
import { setArenaTheme } from '../systems/collision.js';

// Systems
import { initAudio, setupAudioInit, playSound, startMusic, stopMusic } from '../systems/audio.js';
import { createKeyboard, getPlayerInput, applyInputToPlayer, handleJump, checkGamepadConnection, setupHelpToggle } from '../systems/input.js';
import { createCamera, setupCameraFollowMultiplayer, screenShake, resetShake } from '../systems/camera.js';
import { updateBossAI, resetBossAI } from '../systems/ai.js';
import { damagePlayer, updateInvincibility, performAttack, performDodge, createHitParticles } from '../systems/combat.js';
import { destroyLaserBeam } from '../systems/laser.js';

// UI
import { showMainMenu, hideMainMenu } from '../ui/menu.js';
import { updateHUD, updateLevelDisplay, setupHUD, updateControlsHelp, hideLoading, updateControllerStatus, showControllerMessage, clearDefeatedOverlays } from '../ui/hud.js';
import { showLevelIntro, showVictoryMessage, showDefeatMessage, clearModals } from '../ui/modals.js';

// Touch
import { initTouchControls, setTouchControlsVisible, isTouchDevice } from '../systems/touch.js';

// Main Game class
export class Game {
    constructor(canvas) {
        this.canvas = canvas;
        this.app = null;
        this.keyboard = null;
        this.camera = null;
        this.players = [];
        this.boss = null;
        this.minions = [];
        this.minionSpawnTimer = 3;
        this.playerCustomizations = null; // Store player customizations

        // Make app available globally for some systems
        window.app = null;
    }

    // Initialize the game
    init() {
        console.log('Starting Boss Fighting Game...');

        // Create PlayCanvas application
        this.app = new pc.Application(this.canvas);
        this.app.setCanvasResolution(pc.RESOLUTION_AUTO);
        this.app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
        this.app.start();

        // Store globally for systems that need it
        window.app = this.app;
        gameState.app = this.app;

        // Hide loading screen
        hideLoading();

        // Create scene (lights)
        this.createScene();

        // Setup keyboard
        this.keyboard = createKeyboard();
        gameState.keyboard = this.keyboard;

        // Setup camera
        this.camera = createCamera(this.app);
        gameState.camera = this.camera;

        // Setup audio initialization
        setupAudioInit();

        // Setup help toggle
        setupHelpToggle();

        // Setup gamepad detection
        checkGamepadConnection((index) => {
            updateControllerStatus('Connected');
            showControllerMessage('Nintendo Switch Controller');
        });

        // Initialize touch controls (only on touch devices)
        if (isTouchDevice()) {
            initTouchControls();
            setTouchControlsVisible(false); // Hide until game starts
            console.log('Touch device detected - touch controls enabled');
        }

        // Handle screen resize/orientation changes
        this.setupResizeHandler();

        // Add CSS animations
        this.addStyles();

        console.log('Game initialized successfully!');
    }

    // Create scene lighting
    createScene() {
        // Main dramatic light (warm)
        const mainLight = new pc.Entity('MainLight');
        mainLight.addComponent('light', {
            type: 'directional',
            color: new pc.Color(1, 0.95, 0.8),
            intensity: 1.2
        });
        mainLight.setEulerAngles(50, 30, 0);
        this.app.root.addChild(mainLight);

        // Cool fill light
        const fillLight = new pc.Entity('FillLight');
        fillLight.addComponent('light', {
            type: 'directional',
            color: new pc.Color(0.4, 0.5, 0.8),
            intensity: 0.6
        });
        fillLight.setEulerAngles(-30, -60, 0);
        this.app.root.addChild(fillLight);

        // Rim light for silhouettes
        const rimLight = new pc.Entity('RimLight');
        rimLight.addComponent('light', {
            type: 'directional',
            color: new pc.Color(0.3, 0.6, 1.0),
            intensity: 0.4
        });
        rimLight.setEulerAngles(-20, 180, 0);
        this.app.root.addChild(rimLight);

        console.log('Scene created with cinematic lighting');
    }

    // Show main menu
    showMenu() {
        showMainMenu((settings) => {
            this.startGame(settings.playerCount, settings.difficulty, settings.customizations);
        });
    }

    // Start the game with settings
    startGame(playerCount, difficulty, customizations = null) {
        console.log(`Starting game: ${playerCount} player(s), ${difficulty} difficulty`);

        // Show touch controls on touch devices
        if (isTouchDevice()) {
            setTouchControlsVisible(true);
        }

        // Reset game state
        resetGameState();
        setPlayerCount(playerCount);
        setDifficulty(difficulty);

        // Store customizations
        this.playerCustomizations = customizations;

        // Setup HUD for player count
        setupHUD(playerCount);
        updateControlsHelp(playerCount);

        // Create players with customizations
        this.players = [];
        for (let i = 1; i <= playerCount; i++) {
            const playerCustom = customizations ? customizations[i] : null;
            const player = createPlayer(this.app, i, difficulty, playerCustom);
            player._app = this.app;
            this.players.push(player);
        }
        gameState.players = this.players;

        // Setup camera to follow players
        setupCameraFollowMultiplayer(
            this.app,
            this.camera,
            () => this.players,
            () => gameState.isGameStarted
        );

        // Start game loop
        this.startGameLoop();

        // Start first level
        this.startLevel(1);
    }

    // Start a specific level
    startLevel(level) {
        gameState.currentLevel = level;
        const config = LEVEL_CONFIG[level];

        console.log(`Starting Level ${level}: ${config.name}`);

        // Clear existing entities
        this.clearLevel();

        // Show level intro
        showLevelIntro(level, () => {
            // Create arena
            createArena(this.app, this.camera, config.arena);
            setArenaTheme(config.arena); // Set collision bounds for arena shape

            // Create boss
            this.boss = createBoss(this.app, level, gameState.difficulty);
            this.boss._app = this.app;
            gameState.boss = this.boss;

            // Reset players
            clearDefeatedOverlays();
            this.players.forEach(player => {
                resetPlayer(player, gameState.difficulty);
            });

            // Reset boss AI
            resetBossAI();

            // Reset minion spawning
            this.minions = [];
            this.minionSpawnTimer = 3;
            gameState.minions = this.minions;

            // Update UI
            updateLevelDisplay(level);

            // Start the game
            gameState.isGameStarted = true;

            // Start background music for this level
            startMusic(level);
        });
    }

    // Clear level entities
    clearLevel() {
        // Clear minions
        this.minions = clearMinions(this.minions);
        gameState.minions = this.minions;

        // Destroy laser
        destroyLaserBeam();

        // Clear modals
        clearModals();

        // Destroy old boss
        if (this.boss && !this.boss.destroyed) {
            this.boss.destroy();
            this.boss = null;
        }

        // Clear arena
        clearArena();

        // Reset shake
        resetShake();
    }

    // Start the game loop
    startGameLoop() {
        this.app.on('update', (dt) => {
            this.update(dt);
        });

        console.log('Game loop started');
    }

    // Main update loop
    update(dt) {
        if (!gameState.isGameStarted) return;
        if (!this.boss || this.players.length === 0) return;

        // Update each player
        this.players.forEach((player, index) => {
            if (player.health <= 0) return;

            // Get input for this player
            const input = getPlayerInput(this.keyboard, index + 1, dt);

            // Handle jump
            handleJump(player, input);

            // Apply movement
            applyInputToPlayer(player, input, getCollisionObjects(), dt);

            // Handle attack
            if (input.attack && !player.attackPressed) {
                performAttack(this.app, player, this.boss, this.minions, (result) => {
                    if (result === 'victory') {
                        this.handleVictory();
                    }
                });
                player.attackPressed = true;
            }
            if (!input.attack) {
                player.attackPressed = false;
            }

            // Handle dodge
            if (input.dodge && !player.dodgePressed) {
                performDodge(player, this.boss);
                player.dodgePressed = true;
            }
            if (!input.dodge) {
                player.dodgePressed = false;
            }

            // Update invincibility
            updateInvincibility(player, dt);
        });

        // Update boss AI
        updateBossAI(
            this.boss,
            this.players,
            getCollisionObjects(),
            gameState.currentLevel,
            gameState.difficulty,
            dt
        );

        // Update minions
        this.updateMinions(dt);

        // Update UI
        updateHUD(this.players, this.boss, gameState.currentLevel, this.minions);

        // Check for defeat
        if (areAllPlayersDefeated(this.players)) {
            this.handleDefeat();
        }

        // Clear keyboard state
        if (this.keyboard) {
            this.keyboard.update();
        }
    }

    // Update minions
    updateMinions(dt) {
        const level = gameState.currentLevel;

        // Spawn minions at higher levels
        if (level >= 3) {
            this.minionSpawnTimer -= dt;

            const maxMinions = getMaxMinions(level, gameState.difficulty);

            if (this.minionSpawnTimer <= 0 && this.minions.length < maxMinions) {
                const minion = createMinion(this.app, level, gameState.difficulty);
                this.minions.push(minion);
                this.minionSpawnTimer = getSpawnInterval(level);
                playSound('bossCharge');
            }
        }

        // Update each minion
        for (let i = this.minions.length - 1; i >= 0; i--) {
            const minion = this.minions[i];

            if (minion.health <= 0) {
                createHitParticles(this.app, minion.getPosition());
                playSound('hit');
                minion.destroy();
                this.minions.splice(i, 1);
                continue;
            }

            // Get closest living player
            let closestPlayer = null;
            let minDist = Infinity;

            for (const player of this.players) {
                if (player.health <= 0) continue;
                const pos = player.getPosition();
                const mPos = minion.getPosition();
                const dist = Math.sqrt(
                    Math.pow(pos.x - mPos.x, 2) +
                    Math.pow(pos.z - mPos.z, 2)
                );
                if (dist < minDist) {
                    minDist = dist;
                    closestPlayer = player;
                }
            }

            if (closestPlayer) {
                updateMinion(minion, closestPlayer.getPosition(), getCollisionObjects(), dt);

                // Check for attack
                if (canMinionAttack(minion, closestPlayer.getPosition()) && !closestPlayer.isInvincible) {
                    damagePlayer(closestPlayer, minion.damage);
                    minionAttack(minion);

                    if (closestPlayer.health <= 0 && areAllPlayersDefeated(this.players)) {
                        this.handleDefeat();
                    }
                }
            }
        }

        gameState.minions = this.minions;
    }

    // Handle victory
    handleVictory() {
        gameState.isGameStarted = false;

        // Stop the music
        stopMusic();

        // Update HUD one last time to show boss at 0 health
        if (this.boss) {
            this.boss.health = 0;
        }
        updateHUD(this.players, this.boss, gameState.currentLevel, this.minions);

        // Small delay so player can see the boss health bar at 0%
        setTimeout(() => {
            this.clearLevel();

            showVictoryMessage(gameState.currentLevel, (isGameComplete) => {
                if (isGameComplete) {
                    // Restart from level 1
                    this.startLevel(1);
                } else {
                    // Next level
                    this.startLevel(gameState.currentLevel + 1);
                }
            });
        }, 500);
    }

    // Handle defeat
    handleDefeat() {
        if (!gameState.isGameStarted) return; // Prevent multiple triggers

        gameState.isGameStarted = false;

        // Stop the music
        stopMusic();

        // Update HUD one last time to show final state
        updateHUD(this.players, this.boss, gameState.currentLevel, this.minions);

        // Small delay so player can see the defeat state
        setTimeout(() => {
            this.clearLevel();

            showDefeatMessage(
                gameState.currentLevel,
                // Retry same level
                () => {
                    this.startLevel(gameState.currentLevel);
                },
                // Restart game (go to main menu)
                () => {
                    this.restartGame();
                }
            );
        }, 500);
    }

    // Restart the game (go back to main menu)
    restartGame() {
        // Hide touch controls during menu
        if (isTouchDevice()) {
            setTouchControlsVisible(false);
        }

        // Clear everything
        this.clearLevel();

        // Destroy all players
        this.players.forEach(player => {
            if (player && !player.destroyed) {
                player.destroy();
            }
        });
        this.players = [];
        gameState.players = [];

        // Reset game state
        resetGameState();

        // Show main menu
        this.showMenu();
    }

    // Setup handler for screen resize and orientation changes
    setupResizeHandler() {
        const handleResize = () => {
            if (this.app) {
                // Force PlayCanvas to resize to fill window
                this.app.resizeCanvas();
            }
        };

        // Handle window resize
        window.addEventListener('resize', handleResize);

        // Handle orientation change (mobile)
        window.addEventListener('orientationchange', () => {
            // Small delay to let the browser finish rotating
            setTimeout(handleResize, 100);
            setTimeout(handleResize, 300); // Double-check after animation
        });

        // Also handle visibility change (returning to tab)
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                handleResize();
            }
        });
    }

    // Add CSS styles
    addStyles() {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideIn {
                from { transform: translateX(-50%) translateY(-20px); opacity: 0; }
                to { transform: translateX(-50%) translateY(0); opacity: 1; }
            }
            @keyframes flash {
                from { opacity: 0; }
                50% { opacity: 1; }
                to { opacity: 0; }
            }
            @keyframes pulse {
                0% { transform: translate(-50%, -50%) scale(0.9); opacity: 0; }
                50% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
                100% { transform: translate(-50%, -50%) scale(0.9); opacity: 0; }
            }
        `;
        document.head.appendChild(style);
    }
}
